// Central IST datetime helpers — always render in canonical portal format:
//   Date       → DD-MM-YYYY               (e.g. 17-02-2026)
//   Time       → HH:MM:SS AM/PM           (e.g. 10:30:15 AM)
//   Date+Time  → DD-MM-YYYY, HH:MM:SS AM/PM
// All computed in Asia/Kolkata (GMT+05:30).
//
// The global override in `timezoneOverride.js` also enforces these formats
// on any `Date.prototype.toLocale*` / `Intl.DateTimeFormat` call in the app,
// so callers rarely need to import these helpers explicitly. They exist for
// clarity and for null-safe rendering with a friendly fallback.

import { APP_TIMEZONE } from "./timezoneOverride";

const _toDate = (input) => {
  if (input == null || input === "") return null;
  if (input instanceof Date) return isNaN(input) ? null : input;
  const d = new Date(input);
  return isNaN(d) ? null : d;
};

/** "17-02-2026, 10:30:15 AM" (IST) */
export const fmtDateTimeIST = (input, fallback = "—") => {
  const d = _toDate(input);
  if (!d) return fallback;
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

/** "17-02-2026" (IST) */
export const fmtDateIST = (input, fallback = "—") => {
  const d = _toDate(input);
  if (!d) return fallback;
  return d.toLocaleDateString("en-GB");
};

/** "10:30:15 AM" (IST) */
export const fmtTimeIST = (input, fallback = "—") => {
  const d = _toDate(input);
  if (!d) return fallback;
  return d.toLocaleTimeString("en-GB");
};

/** "2026-02-17" — Today's ISO date in IST (safe for date pickers / API params). */
export const todayIST = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
};

export const nowIST = () => new Date();

/** Format a raw 'YYYY-MM-DD' string as 'DD-MM-YYYY' without going through
 *  the Date parser — avoids the browser interpreting date-only strings as
 *  UTC midnight and shifting them by ±05:30 in some locales.
 *  Accepts empty / null / 'YYYY-MM-DDTHH:mm:ss...' inputs too.
 */
export const fmtDateStr = (s, fallback = "—") => {
  if (!s) return fallback;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(s);
};

export { APP_TIMEZONE };

const _istParts = (d, opts) => Object.fromEntries(
  new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", ...opts })
    .formatToParts(d).map((x) => [x.type, x.value]),
);

/** Payroll format "DD/MM/YYYY" (IST). Bare "YYYY-MM-DD" strings are not shifted. */
export const fmtDatePR = (input, fallback = "—") => {
  if (!input) return fallback;
  const bare = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(input));
  if (bare) return `${bare[3]}/${bare[2]}/${bare[1]}`;
  const d = _toDate(input);
  if (!d) return fallback;
  const p = _istParts(d, { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${p.day}/${p.month}/${p.year}`;
};

/** Payroll format "HH:MM AM/PM" (IST). Naive "…THH:MM" / "HH:MM" values are wall-clock IST. */
export const fmtTimePR = (input, fallback = null) => {
  if (!input) return fallback;
  const s = String(input);
  const naive = /^(?:\d{4}-\d{2}-\d{2}T)?(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(s);
  if (naive) {
    let h = Number(naive[1]);
    const ap = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${String(h).padStart(2, "0")}:${naive[2]} ${ap}`;
  }
  const d = _toDate(s);
  if (!d) return fallback;
  const p = _istParts(d, { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${p.hour}:${p.minute} ${(p.dayPeriod || "").toUpperCase()}`;
};

/** Payroll timestamp "DD/MM/YYYY, HH:MM AM/PM" (IST). Naive server timestamps are UTC. */
export const fmtDateTimePR = (input, fallback = "—") => {
  if (!input) return fallback;
  let s = input;
  if (typeof s === "string" && /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s = `${s}Z`;
  const d = _toDate(s);
  if (!d) return fallback;
  const p = _istParts(d, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute} ${(p.dayPeriod || "").toUpperCase()}`;
};
